import { useAppProcessing, useAdditionalDocumentRequest } from '~/pages/ApplicationDetails/context';

import { ActionGroup, Action } from './ActionGroup';
import { DeclineDialog } from './Decline/DeclineDialog';
import { AdditionalDocumentRequest } from './AdditionalDocumentRequest';
import { WaitlistLetterDialog } from './Waitlisted/WaitlistLetterDialog';
import { usePassScreeningActivity } from './passScreening';
import { DeferralRequestDialog } from './DeferralRequest/DeferralRequestDialog';
import { DismissDocumentRequestsDialog } from './DismissDocumentRequests/DismissDocumentRequestsDialog';
import { DismissDocumentRequestOnlyDialog } from './DismissDocumentRequests/DismissDocumentRequestOnlyDialog';
import { OfferLetterDialog, OfferType } from './IssueOffer';
import { ConfirmEnrolmentDialog } from './ConfirmEnrolmentRequest/ConfirmEnrolmentDialog';
import { ManualPaymentDialog } from './ManualPayment';
import { useNavigate } from 'react-router-dom';

export function ApplicationProcessing() {
  const additionalDocumentRequest = useAdditionalDocumentRequest();
  const { activeProcess, startProcess, application, setNextProcess } = useAppProcessing();
  const { submitPassScreening } = usePassScreeningActivity();
  const navigate = useNavigate();

  function onAction(action: Action) {
    switch (action.code) {
      case 'UPDATE_PROGRAM_INTAKE':
        navigate(`/applications/${application.id}/program-edit`);
        return;
      case 'REQUEST_DOCUMENTS':
        return additionalDocumentRequest.enableFeature();
      case 'MARK_AS_PAID_MANUAL':
        return startProcess('markAsPaidManual');
      case 'DISMISS_DOCUMENT_REQUEST':
        return startProcess('dismissDocumentRequestOnly');
    }

    // If there are pending documents in an open request, dismiss them before proceeding into the nextStatus in the array.
    // We require hasOpenDocumentRequests so we don't show this popup after the request was already dismissed
    // (e.g. when some but not all docs were fulfilled, the API can still return additionalDocumentRequest in meta).
    if (
      ['INITIAL_OFFER', 'FINAL_OFFER_ISSUED', 'WAITLISTED', 'READY_FOR_ASSESSMENT', 'DECLINED'].includes(
        action.nextStatus
      ) &&
      application.hasOpenDocumentRequests === true &&
      application.meta.additionalDocumentRequest?.documents.some((f) => f.status.code === 'PENDING')
    ) {
      switch (action.nextStatus) {
        case 'INITIAL_OFFER':
          setNextProcess('issuePreOffer');
          break;
        case 'FINAL_OFFER_ISSUED':
          setNextProcess('issueFinalOffer');
          break;
        case 'WAITLISTED':
          setNextProcess('waitlisted');
          break;
        case 'READY_FOR_ASSESSMENT':
          setNextProcess('passScreening');
          break;
        case 'DECLINED':
          setNextProcess('decline');
          break;
      }
      return startProcess('dismissDocumentRequest');
    }

    // no pending documents, proceed as usual
    switch (action.nextStatus) {
      case 'DECLINED':
        return startProcess('decline');
      case 'INITIAL_OFFER':
        return startProcess('issuePreOffer');
      case 'FINAL_OFFER_ISSUED':
        return startProcess('issueFinalOffer');
      case 'WAITLISTED':
        return startProcess('waitlisted');
      case 'READY_FOR_ASSESSMENT':
        return submitPassScreening();
      case 'ENROLMENT_CONFIRMED':
        return startProcess('confirmEnrolment');
    }
  }

  if (activeProcess === 'additionalDocumentRequest') return <AdditionalDocumentRequest />;

  return (
    <>
      {activeProcess === 'decline' && <DeclineDialog />}
      {activeProcess === 'issuePreOffer' && (
        <OfferLetterDialog
          applicationId={application.id}
          offerType={OfferType.PRE_OFFER}
          reissueOffer={!!application.preOfferLetters}
        />
      )}
      {activeProcess === 'issueFinalOffer' && (
        <OfferLetterDialog
          applicationId={application.id}
          offerType={OfferType.FINAL_OFFER}
          reissueOffer={!!application.finalOfferLetters}
        />
      )}
      {activeProcess === 'waitlisted' && <WaitlistLetterDialog applicationId={application.id} />}
      {activeProcess === 'deferralRequest' && <DeferralRequestDialog />}

      {activeProcess === 'dismissDocumentRequest' && <DismissDocumentRequestsDialog />}
      {activeProcess === 'dismissDocumentRequestOnly' && <DismissDocumentRequestOnlyDialog />}
      {application.status.actionGroups.some((g) => g.actions.length > 0) &&
        application.status.actionGroups.map((actionGroup) => (
          <ActionGroup key={actionGroup.title} actionGroup={actionGroup} onAction={onAction} />
        ))}
      {activeProcess === 'confirmEnrolment' && (
        <ConfirmEnrolmentDialog
          studentFirstName={application.applicant.givenName}
          studentLastName={application.applicant.familyName ?? ''}
        />
      )}
      {activeProcess === 'markAsPaidManual' && <ManualPaymentDialog />}
    </>
  );
}
